import { api } from "./api";
import type {
  DryBulkApproveRequest,
  DryBulkBerth,
  DryBulkCompany,
  DryBulkNominationRequest,
  DryBulkNominationResponse,
  DryBulkRejectRequest,
  DryBulkResource,
  DryBulkStockPile,
} from "./dry-bulk-nomination/types";
import type { VesselNominationHeaderResponse } from "./vessel-nomination/types";

const BFF_VESSEL = "bff-vessel";
const PATH = "vessels/dry-bulk-nominations";

export const DRY_BULK_NOMINATION_QUERY_KEY = "dryBulkNomination";

interface TelemetryOptions {
  transactionId?: string;
}

export const createDryBulkNomination = async (
  payload: DryBulkNominationRequest,
  { transactionId }: TelemetryOptions = {},
) =>
  api.post<DryBulkNominationResponse>(
    BFF_VESSEL,
    PATH,
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );

export const getDryBulkNomination = async (id: string) =>
  api.get<DryBulkNominationResponse>(BFF_VESSEL, `${PATH}/${id}`);

export const getDryBulkNominationVesselHeader = async (id: string) =>
  api.get<VesselNominationHeaderResponse>(BFF_VESSEL, `${PATH}/${id}/vessel-header`);

export const updateDryBulkNomination = async (
  id: string,
  payload: DryBulkNominationRequest,
  { transactionId }: TelemetryOptions = {},
) =>
  api.put<DryBulkNominationResponse>(
    BFF_VESSEL,
    `${PATH}/${id}`,
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );

export const submitDryBulkNomination = async (
  id: string,
  payload: DryBulkNominationRequest,
  { transactionId }: TelemetryOptions = {},
) =>
  api.post<DryBulkNominationResponse>(
    BFF_VESSEL,
    `${PATH}/${id}:submit`,
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );

export const claimDryBulkNominationTask = async (
  id: string,
  { transactionId }: TelemetryOptions = {},
) =>
  api.put<DryBulkNominationResponse>(
    BFF_VESSEL,
    `${PATH}/tasks/${id}:claim`,
    ...(transactionId ? [undefined, { transactionId }] : []),
  );

export const unclaimDryBulkNominationTask = async (
  id: string,
  { transactionId }: TelemetryOptions = {},
) =>
  api.put<DryBulkNominationResponse>(
    BFF_VESSEL,
    `${PATH}/tasks/${id}:unclaim`,
    ...(transactionId ? [undefined, { transactionId }] : []),
  );

export const updateSubmittedDryBulkNomination = async (
  id: string,
  payload: DryBulkNominationRequest,
  { transactionId }: TelemetryOptions = {},
) =>
  api.put<DryBulkNominationResponse>(
    BFF_VESSEL,
    `${PATH}/${id}:update`,
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );

export const cancelDryBulkNomination = async (
  id: string,
  { transactionId }: TelemetryOptions = {},
) =>
  api.put<DryBulkNominationResponse>(
    BFF_VESSEL,
    `${PATH}/${id}:cancel`,
    ...(transactionId ? [undefined, { transactionId }] : []),
  );

export const getDryBulkBerths = async (id: string) =>
  api.get<DryBulkBerth[]>(BFF_VESSEL, `${PATH}/${id}/berths`);

export const approveDryBulkNomination = async (
  id: string,
  payload: DryBulkApproveRequest,
  { transactionId }: TelemetryOptions = {},
) =>
  api.put<DryBulkNominationResponse>(
    BFF_VESSEL,
    `${PATH}/tasks/${id}:approve`,
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );

export const rejectDryBulkNomination = async (
  id: string,
  payload: DryBulkRejectRequest,
  { transactionId }: TelemetryOptions = {},
) =>
  api.put<DryBulkNominationResponse>(
    BFF_VESSEL,
    `${PATH}/tasks/${id}:reject`,
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );

export const replaceDryBulkOperationsResources = async (
  id: string,
  resources: DryBulkResource[],
  { transactionId }: TelemetryOptions = {},
) =>
  api.put<DryBulkNominationResponse>(
    BFF_VESSEL,
    `${PATH}/tasks/${id}/operations-department-resources`,
    resources,
    ...(transactionId ? [{ transactionId }] : []),
  );

export const discardDryBulkNomination = async (
  id: string,
  { transactionId }: TelemetryOptions = {},
) => api.delete(BFF_VESSEL, `${PATH}/${id}`, ...(transactionId ? [{ transactionId }] : []));

export const getDryBulkStockPiles = async (): Promise<DryBulkStockPile[]> => {
  const byCode = await api.get<Record<string, DryBulkStockPile>>(BFF_VESSEL, "stock-piles");
  return Object.values(byCode ?? {});
};

export const getDryBulkCompanies = async (): Promise<DryBulkCompany[]> => {
  const companies = await api.get<DryBulkCompany[]>(BFF_VESSEL, "companies");
  return companies ?? [];
};

export const getDryBulkBerthsByPort = async (portCode: string) =>
  api.get<DryBulkBerth[]>(BFF_VESSEL, `${PATH}/berths`, { params: { portCode } });
